import { type StepperRef, useIntl } from '@capsicum/common';
import { BaseCodeInput, BoldText, CondensedButton } from '@capsicum/common-web';
import { useEmailCodeVerification } from '@capsicum/features/regulations/shared';
import styled from 'styled-components';

const CODE_LENGTH = 6;

type EmailCodeVerificationProps = {
	email: string;
	error?: string;
	onCodeSubmit: (code: string) => void;
	onResendPress: () => void;
	stepperRef: React.RefObject<StepperRef>;
};

export const EmailCodeVerification = ({
	email,
	error: initialError,
	onCodeSubmit,
	onResendPress,
	stepperRef
}: EmailCodeVerificationProps) => {
	const { $t } = useIntl();

	const { code, error, goToFirstStep, reminderCountdown, resendEmailHandler, setCode } = useEmailCodeVerification(
		onResendPress,
		stepperRef,
		initialError,
		onCodeSubmit
	);

	return (
		<S.CodeInputForm>
			<S.Title>{$t('auth.enterCode')}</S.Title>
			<S.Email>
				<S.Subtitle>
					{$t('auth.sentTo', {
						medium: <BoldText>{email}</BoldText>
					})}
				</S.Subtitle>
			</S.Email>
			<S.EmailCodeInput
				codeLength={CODE_LENGTH}
				hasError={Boolean(error)}
				name="email-verification-code-input"
				testID="email-verification-code-input"
				value={code}
				onChange={setCode}
			/>
			{error && <S.EmailCodeErrorText>{error}</S.EmailCodeErrorText>}

			{reminderCountdown.seconds ? (
				<S.ResendCodeText>
					{$t('auth.welcome.codeInput.timerMessageWithEmail', {
						time: reminderCountdown.seconds?.toString().padStart(2, '0')
					})}
				</S.ResendCodeText>
			) : (
				<>
					<S.ResendCodeEnabledText>{$t('auth.welcome.codeInput.retrySMS')}</S.ResendCodeEnabledText>
					<S.ButtonWrapper>
						<CondensedButton
							color="neutral"
							key="send-by-email"
							size="small"
							testID="email-verification-resend-code-button"
							text={$t('auth.sendByEmail2')}
							variant="outlined"
							onClick={resendEmailHandler}
						/>
					</S.ButtonWrapper>
				</>
			)}

			<S.Change onClick={() => goToFirstStep()}>
				{$t('jurisdictionRequirements.emailVerification.changeEmail')}
			</S.Change>
		</S.CodeInputForm>
	);
};

namespace S {
	export const CodeInputForm = styled.div`
		display: flex;
		flex-direction: column;
	`;

	export const EmailCodeInput = styled(BaseCodeInput)`
		justify-content: center;
		margin-bottom: ${({ theme }) => theme.sizing.medium};
	`;

	export const Email = styled.div`
		gap: ${({ theme }) => theme.sizing.xxxsmall};
		margin-bottom: ${({ theme }) => theme.sizing.xlarge};
	`;

	export const Change = styled.p`
		${({ theme }) => theme.fonts.text.text2};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		margin: ${({ theme }) => theme.sizing.xsmall} 0 0;
		cursor: pointer;
		text-align: center;
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const Subtitle = styled.span`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const EmailCodeErrorText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.error.enabled};
		margin-bottom: ${({ theme }) => theme.sizing.xsmall};
	`;

	export const ResendCodeText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.disabled};
	`;

	export const ResendCodeEnabledText = styled.span`
		text-align: center;
		${({ theme }) => theme.fonts.text.text4};
		margin-bottom: ${({ theme }) => theme.sizing.xxxsmall};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		justify-content: center;
	`;
}
